import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import {
  MessageCircle, RefreshCw, Loader2, Copy, Send, CheckCircle2, XCircle,
  Clock, Inbox, BarChart3, FileText, Settings2, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_TEMPLATES = [
  {
    name: 'doctor_medicine_update',
    purpose: 'Daily 11 AM nudge to the medical admin — 5 untouched inventory products (resent 8 PM if no input)',
    body: `Hi {{1}}, doctor input needed: {{2}} medicines are awaiting your clinical notes. Update today's 5 here: {{3}}`,
    variables: ['{{1}} Recipient first name', '{{2}} Total medicines awaiting notes', '{{3}} Task page link (auto-login)'],
  },
  {
    name: 'doctor_pending_reasons',
    purpose: 'Daily 11 AM nudge to each assigned doctor with pending prescription-change reasons (resent 8 PM if no input)',
    body: `Hi {{1}}, {{2}} prescriptions are awaiting your reasoning. Review them here: {{3}}`,
    variables: ['{{1}} Doctor name', '{{2}} Pending reasons count', '{{3}} Pending reasons page link (auto-login)'],
  },
];

const statusBadge = (status) => {
  const map = {
    read: 'border-emerald-300 bg-emerald-50 text-emerald-700',
    delivered: 'border-emerald-200 bg-emerald-50 text-emerald-600',
    sent: 'border-sky-200 bg-sky-50 text-sky-600',
    accepted: 'border-sky-200 bg-sky-50 text-sky-600',
    queued: 'border-amber-200 bg-amber-50 text-amber-600',
    failed: 'border-red-200 bg-red-50 text-red-600',
  };
  return map[status] || 'border-slate-200 text-slate-500';
};

function SendDialog({ open, onClose, templates, onSent }) {
  const [templateName, setTemplateName] = useState('');
  const [phone, setPhone] = useState('');
  const [vars, setVars] = useState({});
  const [sending, setSending] = useState(false);

  const selected = templates.find(t => t.name === templateName);
  const varCount = selected?.params?.body_count || 0;

  const handleSend = async () => {
    if (!phone || !templateName) { toast.error('Phone and template required'); return; }
    setSending(true);
    try {
      const variables = {};
      for (let i = 1; i <= varCount; i++) variables[String(i)] = vars[i] || '';
      await axios.post(`${API_URL}/api/whatsapp/send`, {
        to: phone, template_name: templateName, variables,
        language: selected?.language || 'en_US',
      }, { withCredentials: true });
      toast.success('Message sent');
      onSent();
      onClose();
      setVars({}); setPhone('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="wa-send-dialog">
        <DialogHeader><DialogTitle className="font-heading">Send WhatsApp Message</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Patient Phone (10 digits)</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="98XXXXXXXX" data-testid="wa-send-phone" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Template</Label>
            <Select value={templateName} onValueChange={setTemplateName}>
              <SelectTrigger data-testid="wa-send-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>
                {templates.map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {selected && (
            <p className="text-[11px] text-muted-foreground bg-slate-50 rounded-lg p-2 whitespace-pre-wrap">
              {selected.components?.find(c => c.type === 'BODY')?.text || ''}
            </p>
          )}
          {Array.from({ length: varCount }, (_, i) => i + 1).map(i => (
            <div key={i} className="space-y-1">
              <Label className="text-xs">Variable {`{{${i}}}`}</Label>
              <Input value={vars[i] || ''} onChange={e => setVars({ ...vars, [i]: e.target.value })} data-testid={`wa-send-var-${i}`} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={handleSend} disabled={sending} className="rounded-full bg-[#0E5132] hover:bg-[#063D2F]" data-testid="wa-send-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />} Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WhatsAppPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [prefix, setPrefix] = useState('doctor_');
  const [messages, setMessages] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sendOpen, setSendOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, mRes, sRes, setRes, iRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/whatsapp/templates`, { withCredentials: true }),
        axios.get(`${API_URL}/api/whatsapp/messages`, { params: { limit: 100, status: statusFilter }, withCredentials: true }),
        axios.get(`${API_URL}/api/whatsapp/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/whatsapp/settings`, { withCredentials: true }),
        axios.get(`${API_URL}/api/whatsapp/inbound-messages`, { params: { limit: 50 }, withCredentials: true }),
      ]);
      if (tRes.status === 'fulfilled') { setTemplates(tRes.value.data.templates || []); setPrefix(tRes.value.data.prefix); }
      else toast.error('Could not reach Eden CRM for templates');
      if (mRes.status === 'fulfilled') setMessages(mRes.value.data.items || []);
      if (sRes.status === 'fulfilled') setStats(sRes.value.data);
      if (setRes.status === 'fulfilled') setSettings(setRes.value.data);
      if (iRes.status === 'fulfilled') setInbound(iRes.value.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const allowedTemplates = templates.filter(t => t.allowed);
  const isAdmin = user?.role === 'admin';

  const copyTemplate = (tpl) => {
    navigator.clipboard.writeText(`Name: ${tpl.name}\nLanguage: en_US\nCategory: UTILITY\n\nBody:\n${tpl.body}\n\nVariables:\n${tpl.variables.join('\n')}`);
    toast.success(`${tpl.name} copied — paste it while creating the template in Eden CRM`);
  };

  const saveSettings = async (patch) => {
    try {
      await axios.put(`${API_URL}/api/whatsapp/settings`, patch, { withCredentials: true });
      setSettings(prev => ({ ...prev, ...patch }));
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="whatsapp-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-emerald-600" /> WhatsApp
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Eden CRM WABA · allowed prefix <span className="font-mono text-emerald-700">{prefix}</span></p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchAll} disabled={loading} className="rounded-lg gap-1.5" data-testid="wa-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </Button>
          <Button onClick={() => setSendOpen(true)} disabled={allowedTemplates.length === 0} className="rounded-lg gap-1.5 bg-[#0E5132] hover:bg-[#063D2F]" data-testid="wa-open-send">
            <Send className="w-4 h-4" /> Send Message
          </Button>
        </div>
      </div>

      {allowedTemplates.length === 0 && !loading && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="py-3 px-4 flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            No approved templates with prefix "{prefix}" exist in Eden CRM yet. Copy a suggested template below and create it in the CRM.
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="messages">
        <TabsList className="rounded-full">
          <TabsTrigger value="messages" data-testid="wa-tab-messages" className="rounded-full gap-1.5"><Clock className="w-3.5 h-3.5" /> Messages</TabsTrigger>
          <TabsTrigger value="stats" data-testid="wa-tab-stats" className="rounded-full gap-1.5"><BarChart3 className="w-3.5 h-3.5" /> Stats</TabsTrigger>
          <TabsTrigger value="templates" data-testid="wa-tab-templates" className="rounded-full gap-1.5"><FileText className="w-3.5 h-3.5" /> Templates</TabsTrigger>
          <TabsTrigger value="inbound" data-testid="wa-tab-inbound" className="rounded-full gap-1.5"><Inbox className="w-3.5 h-3.5" /> Replies</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="wa-tab-settings" className="rounded-full gap-1.5"><Settings2 className="w-3.5 h-3.5" /> Settings</TabsTrigger>}
        </TabsList>

        {/* MESSAGES LOG */}
        <TabsContent value="messages" className="space-y-3 mt-4">
          <div className="flex items-center gap-2">
            {['all', 'sent', 'delivered', 'read', 'failed', 'queued'].map(s => (
              <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => setStatusFilter(s)}
                className={`rounded-full text-xs capitalize ${statusFilter === s ? 'bg-[#0E5132] hover:bg-[#063D2F]' : ''}`}
                data-testid={`wa-filter-${s}`}>{s}</Button>
            ))}
          </div>
          {messages.length === 0 ? (
            <Card className="border-dashed border-2"><CardContent className="py-8 text-center text-sm text-muted-foreground">No messages yet</CardContent></Card>
          ) : (
            <div className="grid gap-2">
              {messages.map(m => (
                <Card key={m.id} className="border-slate-200" data-testid={`wa-msg-${m.id}`}>
                  <CardContent className="py-2.5 px-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{m.patient_name || m.to} <span className="text-xs text-muted-foreground font-mono">+{m.to}</span></p>
                        <p className="text-xs text-muted-foreground">
                          {m.template_name} · {m.context === 'rx_auto' ? 'Auto (Rx ready)' : `by ${m.sent_by}`} · {new Date(m.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {m.reply_count > 0 && <Badge variant="outline" className="text-[10px] border-violet-200 text-violet-600">{m.reply_count} repl{m.reply_count > 1 ? 'ies' : 'y'}</Badge>}
                        <Badge variant="outline" className={`text-[10px] capitalize ${statusBadge(m.status)}`}>{m.status}</Badge>
                      </div>
                    </div>
                    {m.status === 'failed' && m.error && (
                      <p className="text-[11px] text-red-500 mt-1 font-mono truncate">{JSON.stringify(m.error).slice(0, 160)}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* STATS */}
        <TabsContent value="stats" className="mt-4">
          {!stats || Object.keys(stats.templates || {}).length === 0 ? (
            <Card className="border-dashed border-2"><CardContent className="py-8 text-center text-sm text-muted-foreground">No sends in the last 7 days</CardContent></Card>
          ) : (
            <div className="grid gap-3">
              {Object.entries(stats.templates).map(([tname, windows]) => (
                <Card key={tname} className="border-slate-200" data-testid={`wa-stats-${tname}`}>
                  <CardHeader className="pb-2 pt-3 px-4"><CardTitle className="text-sm font-mono">{tname}</CardTitle></CardHeader>
                  <CardContent className="px-4 pb-3">
                    <div className="grid grid-cols-3 gap-3">
                      {['24h', '3d', '7d'].map(w => (
                        <div key={w} className="rounded-xl bg-slate-50 p-3">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Last {w}</p>
                          <p className="text-lg font-bold">{windows[w]?.total || 0} <span className="text-[10px] font-normal text-muted-foreground">sent</span></p>
                          <div className="flex items-center gap-2 mt-1 text-[11px]">
                            <span className="text-emerald-600 flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> {windows[w]?.success || 0}</span>
                            <span className="text-red-500 flex items-center gap-0.5"><XCircle className="w-3 h-3" /> {windows[w]?.failed || 0}</span>
                            {(windows[w]?.pending || 0) > 0 && <span className="text-amber-500">{windows[w].pending} pending</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="space-y-5 mt-4">
          <div>
            <h3 className="text-sm font-heading font-semibold mb-2">Suggested Templates <span className="text-xs font-normal text-muted-foreground">— copy &amp; create these in Eden CRM</span></h3>
            <div className="grid gap-2 md:grid-cols-3">
              {SUGGESTED_TEMPLATES.map(tpl => {
                const existsInCrm = templates.some(t => t.name === tpl.name && t.status === 'APPROVED');
                return (
                  <Card key={tpl.name} className={existsInCrm ? 'border-emerald-200 bg-emerald-50/20' : 'border-dashed border-slate-300'} data-testid={`wa-suggested-${tpl.name}`}>
                    <CardHeader className="pb-1 pt-3 px-4">
                      <CardTitle className="text-xs font-mono">{tpl.name}</CardTitle>
                      <CardDescription className="text-[10px]">{tpl.purpose}</CardDescription>
                    </CardHeader>
                    <CardContent className="px-4 pb-3 space-y-2">
                      <p className="text-[11px] text-slate-600 whitespace-pre-wrap bg-white rounded-lg border border-slate-100 p-2 max-h-28 overflow-y-auto">{tpl.body}</p>
                      <div className="flex items-center justify-between">
                        {existsInCrm
                          ? <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200">Live in CRM</Badge>
                          : <Badge variant="outline" className="text-[9px] border-amber-200 text-amber-600">Not in CRM yet</Badge>}
                        <Button size="sm" variant="outline" onClick={() => copyTemplate(tpl)} className="rounded-full text-xs h-7 gap-1" data-testid={`wa-copy-${tpl.name}`}>
                          <Copy className="w-3 h-3" /> Copy
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-heading font-semibold mb-2">Live CRM Catalogue <span className="text-xs font-normal text-muted-foreground">({templates.length} templates · only "{prefix}" usable by this system)</span></h3>
            <div className="grid gap-1.5">
              {templates.map(t => (
                <Card key={t.name} className={`border ${t.allowed ? 'border-emerald-200' : 'border-slate-100 opacity-70'}`} data-testid={`wa-tpl-${t.name}`}>
                  <CardContent className="py-2 px-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-mono font-medium">{t.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate max-w-2xl">{t.components?.find(c => c.type === 'BODY')?.text || ''}</p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className="text-[9px]">{t.params?.body_count || 0} vars</Badge>
                      <Badge variant="outline" className={`text-[9px] ${t.status === 'APPROVED' ? 'border-emerald-200 text-emerald-600' : 'border-amber-200 text-amber-600'}`}>{t.status}</Badge>
                      {t.allowed && <Badge className="text-[9px] bg-[#E6EFEA] text-[#0E5132] border-[rgba(14,81,50,0.15)]">Usable</Badge>}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* INBOUND REPLIES */}
        <TabsContent value="inbound" className="mt-4">
          {inbound.length === 0 ? (
            <Card className="border-dashed border-2"><CardContent className="py-8 text-center text-sm text-muted-foreground">No patient replies yet. Register the inbound webhook in Eden CRM to receive them.</CardContent></Card>
          ) : (
            <div className="grid gap-2">
              {inbound.map(m => (
                <Card key={m.id} className="border-slate-200">
                  <CardContent className="py-2.5 px-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium">{m.patient_name || m.from_name || m.wa_id}</p>
                        <p className="text-xs text-slate-600">{m.text}</p>
                        {m.in_reply_to_template && <p className="text-[10px] text-muted-foreground">↩ reply to {m.in_reply_to_template}</p>}
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0">{new Date(m.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SETTINGS */}
        {isAdmin && settings && (
          <TabsContent value="settings" className="mt-4 space-y-4 max-w-2xl">
            <Card className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading">Daily Medicine Update Nudge</CardTitle>
                <CardDescription className="text-xs">11:00 AM IST — sends 5 untouched inventory products to the medical admin with a task link. Resends 8:00 PM IST if no input.</CardDescription>
              </CardHeader>
              <CardContent className="px-5 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Enable daily nudge</Label>
                  <Switch checked={settings.medicine_nudge_enabled} onCheckedChange={(v) => saveSettings({ medicine_nudge_enabled: v })} data-testid="wa-mednudge-toggle" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Recipient phone (medical admin)</Label>
                  <Input defaultValue={settings.medicine_recipient_phone} onBlur={(e) => e.target.value !== settings.medicine_recipient_phone && saveSettings({ medicine_recipient_phone: e.target.value })} data-testid="wa-mednudge-phone" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Template</Label>
                  <Input defaultValue={settings.medicine_template_name} onBlur={(e) => e.target.value !== settings.medicine_template_name && saveSettings({ medicine_template_name: e.target.value })} data-testid="wa-mednudge-template" />
                </div>
                <Button size="sm" variant="outline" className="rounded-full text-xs gap-1" data-testid="wa-mednudge-sendnow"
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API_URL}/api/whatsapp/run-nudge/medicine`, {}, { withCredentials: true });
                      toast.info(data.skipped ? `Skipped: ${data.skipped}` : `Result: ${data.status || 'ok'}`);
                      fetchAll();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}>
                  <Send className="w-3 h-3" /> Send now (test)
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading">Daily Pending Reasons Nudge</CardTitle>
                <CardDescription className="text-xs">11:00 AM IST — each assigned doctor with pending prescription reasons gets a WhatsApp with their count + review link (phone from their profile). Resends 8:00 PM IST if no progress.</CardDescription>
              </CardHeader>
              <CardContent className="px-5 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Enable daily nudge</Label>
                  <Switch checked={settings.reasons_nudge_enabled} onCheckedChange={(v) => saveSettings({ reasons_nudge_enabled: v })} data-testid="wa-rsnnudge-toggle" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Template</Label>
                  <Input defaultValue={settings.reasons_template_name} onBlur={(e) => e.target.value !== settings.reasons_template_name && saveSettings({ reasons_template_name: e.target.value })} data-testid="wa-rsnnudge-template" />
                </div>
                <Button size="sm" variant="outline" className="rounded-full text-xs gap-1" data-testid="wa-rsnnudge-sendnow"
                  onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API_URL}/api/whatsapp/run-nudge/reasons`, {}, { withCredentials: true });
                      toast.info(data.skipped ? `Skipped: ${data.skipped}` : `Sent to ${data.results?.length || 0} doctor(s)`);
                      fetchAll();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }}>
                  <Send className="w-3 h-3" /> Send now (test)
                </Button>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading">Prescription Ready — Auto Send</CardTitle>
                <CardDescription className="text-xs">When a doctor finalizes a prescription, the patient automatically gets a WhatsApp with a secure PDF link (variables: 1=patient name, 2=doctor, 3=link)</CardDescription>
              </CardHeader>
              <CardContent className="px-5 pb-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Enable auto-send</Label>
                  <Switch checked={settings.rx_auto_send} onCheckedChange={(v) => saveSettings({ rx_auto_send: v })} data-testid="wa-rx-autosend-toggle" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Template</Label>
                  <Select value={settings.rx_template_name || ''} onValueChange={(v) => saveSettings({ rx_template_name: v })}>
                    <SelectTrigger data-testid="wa-rx-template-select"><SelectValue placeholder={`Choose a ${prefix} template`} /></SelectTrigger>
                    <SelectContent>
                      {allowedTemplates.map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {allowedTemplates.length === 0 && <p className="text-[11px] text-amber-600">Create a "{prefix}" template in Eden CRM first (see Templates tab)</p>}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm font-heading">Webhooks to register in Eden CRM</CardTitle>
                <CardDescription className="text-xs">Signature verification: {settings.webhook_secret_configured ? 'enabled' : 'not configured (add WABA_WEBHOOK_SECRET to enable)'}</CardDescription>
              </CardHeader>
              <CardContent className="px-5 pb-4 space-y-2">
                {['status', 'inbound'].map(ep => (
                  <div key={ep} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2">
                    <code className="text-[11px]">https://doctor.eden-office.in/api/whatsapp/{ep}</code>
                    <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => { navigator.clipboard.writeText(`https://doctor.eden-office.in/api/whatsapp/${ep}`); toast.success('Copied'); }}>
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <SendDialog open={sendOpen} onClose={() => setSendOpen(false)} templates={allowedTemplates} onSent={fetchAll} />
    </div>
  );
}
